import { create } from 'zustand';
import { fetchUserState, linkIdentity } from '../api/user';
import type { FeatureAvailability, UserState } from '../types';
import { handleUserApiError, showSuccessNotification } from '../utils/errorHandler';
import { trackSignUp } from '../services/analyticsService';

interface UserStore {
  state: UserState;
  user: { id: string; username: string } | null;
  preferences: Record<string, string | null>;
  features: Record<string, FeatureAvailability>;
  marketingOnboardingCompleted: boolean;
  hasAcceptedCurrentTerms: boolean;
  isLoading: boolean;
  error: string | null;
  errorCode: string | null;
  isCheckingState: boolean;
  checkUserState: (getToken: () => Promise<string>) => Promise<void>;
  linkIdentity: (getToken: () => Promise<string>, action: 'link' | 'nolink', auth0Sub?: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export const useUserStore = create<UserStore>()((set, get) => ({
  state: 'loading',
  user: null,
  preferences: {},
  features: {},
  marketingOnboardingCompleted: false,
  hasAcceptedCurrentTerms: false,
  isLoading: false,
  error: null,
  errorCode: null,
  isCheckingState: false,

  checkUserState: async (getToken: () => Promise<string>) => {
    // Prevent concurrent state checks
    if (get().isCheckingState) {
      return;
    }

    set({ isLoading: true, error: null, errorCode: null, isCheckingState: true });

    try {
      // Try to get the token first - this will throw if Auth0 can't refresh
      let token: string;
      try {
        token = await getToken();
      } catch (tokenError: any) {
        // Auth0 token retrieval failed (e.g., refresh token invalid)
        console.error('Auth0 token retrieval failed:', tokenError);

        const isAuth0TokenError =
          tokenError.message?.includes('Unknown or invalid refresh token') ||
          tokenError.message?.includes('invalid_token') ||
          tokenError.message?.includes('login_required') ||
          tokenError.message?.includes('Missing Refresh Token') ||
          tokenError.error === 'login_required';

        if (isAuth0TokenError) {
          console.log('Auth0 refresh token invalid, redirecting to logout');
          set({ error: 'Authentication expired', isLoading: false, isCheckingState: false });

          // Redirect to logout immediately to clear invalid tokens
          setTimeout(() => {
            window.location.href = '/logout';
          }, 500);
          return;
        } else {
          // Re-throw other token errors
          throw tokenError;
        }
      }

      const response = await fetchUserState(() => Promise.resolve(token));
      if (response.accountCreated && response.user) trackSignUp(response.user.id);
      set({
        state: response.state,
        user: (response as any).user || null,
        preferences: response.preferences || {},
        features: response.features || {},
        marketingOnboardingCompleted: response.marketingOnboardingCompleted ?? false,
        hasAcceptedCurrentTerms: response.hasAcceptedCurrentTerms ?? false,
        errorCode: null,
        isLoading: false,
        isCheckingState: false,
      });
    } catch (error: any) {
      console.error('Error checking user state:', error);

      // Check if this is an Auth0 token error (refresh token invalid/expired)
      const isAuth0TokenError =
        error.message?.includes('Unknown or invalid refresh token') ||
        error.message?.includes('invalid_token') ||
        error.message?.includes('login_required') ||
        error.message?.includes('Missing Refresh Token');

      // Handle specific error cases
      if (error.response?.status === 401 || isAuth0TokenError) {
        // User needs to re-authenticate - redirect to logout to clear tokens
        console.log('Authentication failed, redirecting to logout to clear tokens');
        handleUserApiError(error, 'checking user state');
        set({ error: 'Authentication required', isLoading: false, isCheckingState: false });

        // Redirect to logout to clear invalid tokens and force re-authentication
        setTimeout(() => {
          window.location.href = '/logout';
        }, 1000);
      } else if (error.response?.status === 403) {
        if (error.response?.data?.code === 'email_not_verified') {
          set({
            error: 'Your Auth0 account did not complete email verification.',
            errorCode: 'email_not_verified',
            isLoading: false,
            isCheckingState: false,
          });
          return;
        }

        // Extract state from error response if available
        const userState = error.response?.data?.userState;
        if (userState) {
          set({ state: userState, isLoading: false, isCheckingState: false });
        } else {
          handleUserApiError(error, 'checking user state');
          set({ error: 'Access denied', isLoading: false, isCheckingState: false });
        }
      } else {
        handleUserApiError(error, 'checking user state');
        set({ error: 'Failed to check user state', isLoading: false, isCheckingState: false });
      }
    }
  },

  linkIdentity: async (getToken: () => Promise<string>, action: 'link' | 'nolink', auth0Sub?: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await linkIdentity(getToken, action, auth0Sub);
      if (response.accountCreated && response.user) trackSignUp(response.user.id);
      showSuccessNotification(
        action === 'link' ? 'Account Linked' : 'Account Created',
        action === 'link' ? 'Your accounts have been successfully linked!' : 'Your new account has been created!',
      );
      set({
        state: response.state,
        user: response.user || get().user,
        isLoading: false,
      });
    } catch (error: any) {
      console.error('Error linking identity:', error);
      handleUserApiError(error, 'linking identity');
      set({ error: 'Failed to link identity', isLoading: false });
    }
  },

  clearError: () => set({ error: null, errorCode: null }),

  reset: () =>
    set({
      state: 'loading',
      user: null,
      preferences: {},
      features: {},
      marketingOnboardingCompleted: false,
      hasAcceptedCurrentTerms: false,
      isLoading: false,
      error: null,
      errorCode: null,
      isCheckingState: false,
    }),
}));
